import { useState } from "react";
import { Form, Link, useRouteLoaderData } from "react-router-dom";

import { AsyncFormSelect } from "../forms/records/form/AsyncFormSelect";
import { SelectOption } from "../forms/types";
import { useAuthStore, User } from "../main/authStore";

import { defaultRentalRecord, RentalRecord } from "./types";

type RentalFormState = RentalRecord & {
    project: SelectOption | null;
    renter: RentalRecord["renter"] | SelectOption | null;
};

export default function RentalForm({ keyword }: { keyword: "Add" | "Edit" }) {
    const currentUser = useAuthStore(state => state.user) as User;
    const existing = useRouteLoaderData("Rental") as { rental?: RentalRecord } | undefined;
    const [rental, setRental] = useState<RentalFormState>(
        existing?.rental ? {
            ...existing.rental,
            project: existing.rental.projectId ? { value: String(existing.rental.projectId), label: existing.rental.projectName } : null,
            renter: existing.rental.renter ?? (existing.rental.renterId ? { value: existing.rental.renterId, label: existing.rental.renterName, department: existing.rental.department } : null),
        } : {
            ...defaultRentalRecord,
            project: null,
            renter: {
                value: currentUser.id,
                label: currentUser.userName,
                department: currentUser.department,
            },
            department: currentUser.department,
        }
    );

    return (
        <Form method = "post" className = "my-3">
            {keyword === "Edit" ? <input type = "hidden" name = "id" value = {rental.id ?? ""} /> : null}
            <input type = "hidden" name = "action" value = {keyword} />
            <div className = "blk5 mb-2 d-flex align-items-center">
                <label className = "fw-bold me-3 required-field" htmlFor = "equipmentName">Equipment</label>
                <input
                    required
                    id = "equipmentName"
                    name = "equipmentName"
                    className = "form-control"
                    value = {rental.equipmentName}
                    onChange = {e => setRental({ ...rental, equipmentName: e.currentTarget.value })}
                />
            </div>
            <div className = "blk5 mb-2 d-flex align-items-center">
                <label className = "fw-bold me-3 required-field" htmlFor = "projectId">Project</label>
                <div style = {{ minWidth: 240, flexGrow: 1 }}>
                    <AsyncFormSelect
                        placeholder = "Search project..."
                        required = {true}
                        name = "projectId"
                        inputId = "projectId"
                        fileName = "common/searchProject.php"
                        value = {rental.project}
                        onChange = {next => setRental({ ...rental, project: next as SelectOption })}
                    />
                </div>
            </div>
            <div className = "blk5 mb-2 d-flex align-items-center">
                <label className = "fw-bold me-3 required-field" htmlFor = "renterId">Technician</label>
                <div style = {{ minWidth: 240, flexGrow: 1 }}>
                    <AsyncFormSelect
                        placeholder = "Search user..."
                        required = {true}
                        name = "renterId"
                        inputId = "renterId"
                        fileName = "common/searchUser.php"
                        value = {rental.renter}
                        onChange = {next => {
                            const option = next as SelectOption & { department?: string };
                            setRental({
                                ...rental,
                                renter: option,
                                department: option?.department ?? rental.department,
                            });
                        }}
                    />
                </div>
            </div>
            <div className = "blk5 mb-2 d-flex align-items-center">
                <label className = "fw-bold me-3 required-field" htmlFor = "department">Department</label>
                <input
                    required
                    id = "department"
                    name = "department"
                    className = "form-control"
                    value = {rental.department}
                    onChange = {e => setRental({ ...rental, department: e.currentTarget.value })}
                />
            </div>
            <div className = "blk5 align-items-center mb-2">
                <div className = "d-flex align-items-center mb-2 me-3">
                    <label className = "fw-bold me-3 required-field" htmlFor = "rentalStartDate">Start</label>
                    <input
                        required
                        type = "date"
                        id = "rentalStartDate"
                        name = "rentalStartDate"
                        max = {rental.rentalExpireDate || "2999-12-31"}
                        className = "form-control"
                        value = {rental.rentalStartDate}
                        onChange = {e => setRental({ ...rental, rentalStartDate: e.currentTarget.value })}
                    />
                </div>
                <div className = "d-flex align-items-center mb-2">
                    <label className = "fw-bold me-3" htmlFor = "rentalExpireDate">Expire</label>
                    <input
                        type = "date"
                        id = "rentalExpireDate"
                        name = "rentalExpireDate"
                        min = {rental.rentalStartDate || "1900-01-01"}
                        className = "form-control"
                        value = {rental.rentalExpireDate}
                        onChange = {e => setRental({ ...rental, rentalExpireDate: e.currentTarget.value })}
                    />
                </div>
            </div>
            <label className = "mb-2 fw-bold" htmlFor = "notes">Notes</label>
            <textarea
                id = "notes"
                name = "notes"
                className = "form-control mb-2"
                value = {rental.notes}
                onChange = {e => setRental({ ...rental, notes: e.currentTarget.value })}
                style = {{ width: "100%" }}
            />
            <div>
                <button className = "btn btn-primary me-3">{keyword}</button>
                <Link to = ".." className = "btn btn-outline-secondary float-end">Back</Link>
            </div>
        </Form>
    );
}
